import {
    LazyQueryResultTuple,
    MutationHookOptions,
    MutationTuple,
    OperationVariables,
    QueryHookOptions,
    QueryResult,
    useApolloClient,
    useLazyQuery,
    useMutation,
    useQuery
} from '@apollo/client'
import { DocumentNode } from 'graphql'
import jwt_decode from 'jwt-decode'
import { useEffect } from 'react'

// types
import { USER_DATA } from '../types/userData.interface'

// graphql
import { GET_USER } from '../graphql/queries/getUser.query'

// context
import { useAuth } from './../context/AuthContext/AuthContext'

// local storage
import {
    LOCAL_STORAGE_getToken,
    LOCAL_STORAGE_removeToken,
    LOCAL_STORAGE_setToken
} from '../localStorage/token.localStorage'

// routes
import mapVehiclesWithPlates from '../utils/mapVehiclesWithPlates.utils'
import { profileRoute } from '../views/Profile/route.const'
import { homeRoute } from '@views/Home/route.const'

export const useIsLoggedIn = () => {
    const { isLoggedIn } = useAuth()
    return isLoggedIn
}

export const useLogin = () => {
    const { setToken, setUser } = useAuth()
    const client = useApolloClient()
    const auth0Domain = process.env.VITE_AUTH0_DOMAIN
    const auth0ClientId = process.env.VITE_AUTH0_CLIENT_ID
    const auth0Audience = process.env.VITE_AUTH0_AUDIENCE

    return async (email: string, password: string, history: any, autoLogin = false) => {
        // get return url from url query params
        const returnUrl =
            new URLSearchParams(window.location.search).get('returnUrl') ||
            new URLSearchParams(window.location.search).get('redirectUrl')
        const url = `https://${auth0Domain}/oauth/token`
        return fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                grant_type: 'password',
                realm: 'Username-Password-Authentication',
                username: email,
                password,
                audience: auth0Audience,
                scope: 'openid profile email',
                client_id: auth0ClientId
            })
        })
            .then((response) => response.json())
            .then((data) => {
                // const accessToken: string = data.access_token
                if (data && data.id_token) {
                    LOCAL_STORAGE_setToken(data.id_token)
                    const decodedUser: { email: string; family_name: string; given_name: string } = jwt_decode(
                        data.id_token
                    )
                    setUser({
                        email: decodedUser.email,
                        first_name: decodedUser.given_name,
                        last_name: decodedUser.family_name
                    } as USER_DATA)
                    setToken(data.id_token)
                    const redirectUrl = autoLogin ? `${profileRoute()}?autoLogin=true` : homeRoute()

                    history.push(returnUrl ? decodeURIComponent(returnUrl) : redirectUrl)
                    client.clearStore()
                    return data.id_token
                } else {
                    return LOCAL_STORAGE_getToken()
                }
            })
            .catch(() => {
                return LOCAL_STORAGE_getToken()
            })
    }
}

export const useLogout = () => {
    const { setToken, setUser } = useAuth()
    const client = useApolloClient()

    return () => {
        setToken(null)
        setUser(null)
        LOCAL_STORAGE_removeToken()
        client.clearStore()
    }
}

export function useAuthenticatedQuery<TData = any, TVariables extends OperationVariables = OperationVariables>(
    options: UseAuthenticatedOptions<TData, TVariables>
): QueryResult<TData, TVariables> {
    const { query, variables, ...otherOptions } = options
    const { token } = useAuth()

    return useQuery<TData, TVariables>(query, {
        variables,
        context: {
            headers: {
                ...(token && { Authorization: `Bearer ${token}` }) // Conditionally add the token
            }
        },
        fetchPolicy: 'no-cache',
        ...otherOptions
    })
}

export function useLazyAuthenticatedQuery<TData = any, TVariables extends OperationVariables = OperationVariables>(
    options: UseAuthenticatedOptions<TData, TVariables>
): LazyQueryResultTuple<TData, TVariables> {
    const { query, ...otherOptions } = options
    const { token } = useAuth()

    return useLazyQuery<TData, TVariables>(query, {
        ...otherOptions,
        context: {
            headers: {
                ...(token && { Authorization: `Bearer ${token}` }) // Conditionally add the token
            }
        },
        fetchPolicy: 'no-cache'
    })
}

export const useGetUser = () => {
    const { token, user, setUser } = useAuth()

    const { data, loading, error } = useAuthenticatedQuery<{
        get_user_data: USER_DATA
    }>({
        query: GET_USER,
        fetchPolicy: 'no-cache',
        skip: !!(user && user.license_plates) || !token // Skip this query if we already have the user or if there is no token
    })

    useEffect(() => {
        if (!loading && data && data.get_user_data) {
            const { license_plates, vehicles } = data.get_user_data
            const existingVehicles = mapVehiclesWithPlates(vehicles, license_plates)
            setUser({
                ...data.get_user_data,
                vehicles: existingVehicles
            }) // Set user in your auth context
        }
    }, [loading, data, setUser, user])

    if (loading) return { user, loading: true }
    if (error) return { user: null, loading: false, error }

    return { user: user || data?.get_user_data, loading: false }
}

interface UseAuthenticatedOptions<TData, TVariables extends OperationVariables>
    extends Omit<QueryHookOptions<TData, TVariables>, 'context'> {
    query: DocumentNode
}

interface UseAuthenticatedMutationOptions<TData, TVariables>
    extends Omit<MutationHookOptions<TData, TVariables>, 'context'> {
    mutation: DocumentNode
}

export function useAuthenticatedMutation<TData = any, TVariables = OperationVariables>(
    options: UseAuthenticatedMutationOptions<TData, TVariables>
): MutationTuple<TData, TVariables> {
    const { mutation, variables, ...otherOptions } = options
    const { token } = useAuth()

    return useMutation<TData, TVariables>(mutation, {
        ...otherOptions,
        variables,
        context: {
            headers: {
                ...(token && { Authorization: `Bearer ${token}` }) // Conditionally add the token
            }
        }
    })
}
